import { useCallback, useEffect, useState } from 'react';
import type { RailState } from '@/types/leftNavRail.types';

export const RAIL_STATE_STORAGE_KEY = 'egem.ui.leftNavRail.state';

// The rail is expanded by default so navigation labels are visible without
// requiring the user to toggle it open, keeping destinations discoverable for
// keyboard and screen-reader users. Returning users keep their last choice via
// the persisted localStorage value.
const DEFAULT_STATE: RailState = 'expanded';

export interface UseRailStateResult {
  readonly state: RailState;
  readonly toggle: () => void;
  readonly setState: (next: RailState) => void;
}

function isValidRailState(value: unknown): value is RailState {
  return value === 'expanded' || value === 'collapsed';
}

function readInitial(): RailState {
  if (typeof window === 'undefined') {
    return DEFAULT_STATE;
  }
  try {
    const raw = window.localStorage.getItem(RAIL_STATE_STORAGE_KEY);
    return isValidRailState(raw) ? raw : DEFAULT_STATE;
  } catch {
    return DEFAULT_STATE;
  }
}

function writePersisted(next: RailState): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(RAIL_STATE_STORAGE_KEY, next);
  } catch {
    /* no-op: persistence is best-effort */
  }
}

export function useRailState(): UseRailStateResult {
  const [state, setRailState] = useState<RailState>(readInitial);

  useEffect(() => {
    writePersisted(state);
  }, [state]);

  const toggle = useCallback(() => {
    setRailState((prev) => (prev === 'expanded' ? 'collapsed' : 'expanded'));
  }, []);

  const setState = useCallback((next: RailState) => {
    setRailState(next);
  }, []);

  return { state, toggle, setState };
}
